import { Component, computed, effect, inject, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MedicalServiceBatchPrintPageStore } from './medical-service-batch-print-page.store';
import { fb, router, translate } from '../../../../../modules/shared/shared.global-service';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import { FilterBuilder } from '../../../../../modules/content/helper/filter.builder';
import { FilterInput } from '@it2go/data-grid';
import { TableColumn } from '../../../../../modules/shared/components/kit/table/table.types';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { PrintVoucherDialogComponent } from '../../../voucher/print-voucher-dialog/print-voucher-dialog.component';
import { TableComponent } from '../../../../../modules/shared/components/kit/table/table.component';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { toSignal } from '@angular/core/rxjs-interop';
import { SubscriptionComponent } from '../../../../../modules/shared/helpers/subscription.component';
import { voucherDetailUri } from '../../../../../utils/router.utils';
import { VoucherGridItemObject } from '@it2go/types';
import { voucherWithPersonFrag } from '../../../../../services/types/voucher.types';
import { clientChipField, dateField, doctorChipField } from '../../../../../modules/shared/components/kit/table/table.fields';
import { BaseDialogService } from '../../../../kit/base-dialog/base-dialog-service';
import { patchFormOnlyOnce } from '../../../../../utils/form.utils';

@Component({
    selector: 'app-medical-service-batch-print-page',
    templateUrl: 'medical-service-batch-print-page.component.html',
    styleUrl: 'medical-service-batch-print-page.component.scss',
    providers: [MedicalServiceBatchPrintPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectInputComponent,
        DateInputComponent,
        ButtonComponent,
        TooltipModule,
        TableComponent,
    ],
})
export class MedicalServiceBatchPrintPageComponent extends SubscriptionComponent {

    public store = inject(MedicalServiceBatchPrintPageStore);
    public dialogService = inject(BaseDialogService);

    protected form = fb.group({
        serviceId: fb.control<number | null>(null),
        workplaceId: fb.control<number | null>(null),
        validAt: fb.control<Date | null>(null),
    });

    protected additionalFilter = signal<FilterInput[][]>([]);

    protected serviceId = toSignal(this.form.controls.serviceId.valueChanges);
    protected workplaces = computed(() => {
        return this.store.globalGlobalStore.organizationStore.servicesInContext()?.find((it) => {
            return it.id === this.serviceId();
        })?.workplaces?.map((it) => {
            return {
                name: it.name,
                value: it.id,
            };
        }) || [];
    });

    @ViewChild(TableComponent)
    protected table!: TableComponent;

    protected columns: TableColumn[] = [
        clientChipField('folder', 'folder.person', false),
        { name: 'type' },
        { name: 'sequenceNumber' },
        dateField('issueDate', false, false),
        dateField('validTo', false, false),
        { name: 'insuranceAbbr' },
        {
            name: 'primaryDiagnosis',
            data: (item: any) => item.code,
        },
        doctorChipField('doctor', { filterable: false, sortable: false }),
    ];

    constructor() {
        super();
        patchFormOnlyOnce(this.store.globalGlobalStore.loggedUserStore.selectedServiceId, this.form, 'serviceId');
        effect(() => {
            this.store.globalGlobalStore.loggedUserStore.setSelectedServiceId(this.serviceId()).then();
        });
        effect(() => {
            this.additionalFilter.set(FilterBuilder.filter({
                organizationId: this.store.globalGlobalStore.organizationStore.selectedId(),
            }).filter || []);
        });
        effect(() => {
            this.form.patchValue({ workplaceId: null });
        });
        this.subs.push(
            this.form.valueChanges.subscribe((values) => {
                const builder = new FilterBuilder();
                if (values.workplaceId) {
                    builder.where('workplaceId', values.workplaceId);
                } else if (values.serviceId) {
                    builder.where('serviceId', values.serviceId);
                }
                if (values.validAt) {
                    builder.where('voucherFrom', values.validAt.toISOString(), 'LTE');
                    builder.where('voucherTo', values.validAt.toISOString(), 'GTE');
                }

                this.additionalFilter.set(builder.filter().filter || []);
            }),
        );
    }

    protected print(): void {
        this.dialogService.open(
            PrintVoucherDialogComponent,
            {
                header: translate.instant('common.export'),
                data: { voucherIds: this.table.selection().map((it: any) => it.id) },
            },
        );
    }

    protected async detail(item: VoucherGridItemObject): Promise<void> {
        await router.navigateByUrl(voucherDetailUri(item, item.folder.serviceId));
    }

    protected readonly voucherWithPersonFrag = voucherWithPersonFrag;
}
